import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  FormHelperText
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#1A1A1A',
    border: '1px solid #333',
    borderRadius: '12px',
    maxWidth: '800px',
    width: '100%',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#121212',
    '& fieldset': {
      borderColor: '#333',
    },
    '&:hover fieldset': {
      borderColor: '#4CAF50',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4CAF50',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#ccc',
    '&.Mui-focused': {
      color: '#4CAF50',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#fff',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#121212',
    '& fieldset': {
      borderColor: '#333',
    },
    '&:hover fieldset': {
      borderColor: '#4CAF50',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4CAF50',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#ccc',
    '&.Mui-focused': {
      color: '#4CAF50',
    },
  },
  '& .MuiSelect-select': {
    color: '#fff',
  },
}));

const AnswerBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#121212',
  border: '1px solid #333',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
}));

const QuestionEditModal = ({ 
  open, 
  onClose, 
  question, 
  onSave,
  validationErrors = []
}) => {
  const [formData, setFormData] = useState({
    questionText: '',
    answerType: '',
    difficulty: '',
    questionMood: '',
    questionTopic: '',
    questionBank: '',
    timeLimit: 30,
    questionClue: '',
    answers: []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (question) {
      setFormData({
        questionText: question.questionText || '',
        answerType: question.answerType || '',
        difficulty: question.difficulty || '',
        questionMood: question.questionMood || '',
        questionTopic: question.questionTopic || '',
        questionBank: question.questionBank || 'Songq',
        timeLimit: question.timeLimit || 30,
        questionClue: question.questionClue || '',
        answers: question.answers || []
      });
    }
  }, [question]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAnswerChange = (index, field, value) => {
    const updatedAnswers = [...formData.answers];
    updatedAnswers[index] = {
      ...updatedAnswers[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      answers: updatedAnswers
    }));
  };

  const addAnswer = () => {
    if (formData.answers.length < 6) {
      setFormData(prev => ({
        ...prev,
        answers: [
          ...prev.answers,
          { text: '', correct: false, reason: '' }
        ]
      }));
    }
  };

  const removeAnswer = (index) => {
    if (formData.answers.length > 2) {
      const updatedAnswers = formData.answers.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        answers: updatedAnswers
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.questionText || formData.questionText.trim().length < 10) {
      newErrors.questionText = 'Question text must be at least 10 characters';
    }

    if (!formData.answerType) {
      newErrors.answerType = 'Answer type is required';
    }

    if (!formData.difficulty) {
      newErrors.difficulty = 'Difficulty is required';
    }

    if (formData.answers.length < 2) {
      newErrors.answers = 'At least 2 answers are required';
    }

    const correctAnswers = formData.answers.filter(a => a.correct).length;
    if (correctAnswers === 0) {
      newErrors.answers = 'At least one answer must be correct';
    }

    if (formData.answerType === 'single_choice' && correctAnswers !== 1) {
      newErrors.answers = 'Single choice questions must have exactly one correct answer';
    }

    if (formData.answerType === 'true_false' && formData.answers.length !== 2) {
      newErrors.answers = 'True/False questions must have exactly 2 answers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        color: '#fff', 
        borderBottom: '1px solid #333',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6">Edit Question</Typography>
        <IconButton onClick={handleClose} sx={{ color: '#ccc' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
            <Typography sx={{ color: '#fff', fontWeight: 600 }}>Validation Errors:</Typography>
            {validationErrors.map((error, index) => (
              <Typography key={index} sx={{ color: '#ffcdd2', mt: 0.5 }}>
                • {error}
              </Typography>
            ))}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Question Text */}
          <StyledTextField
            fullWidth
            label="Question Text"
            multiline
            rows={3}
            value={formData.questionText}
            onChange={(e) => handleFieldChange('questionText', e.target.value)}
            error={!!errors.questionText}
            helperText={errors.questionText}
          />

          {/* Form Fields Row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <StyledFormControl error={!!errors.answerType}>
              <InputLabel>Answer Type</InputLabel>
              <Select
                value={formData.answerType}
                onChange={(e) => handleFieldChange('answerType', e.target.value)}
                label="Answer Type"
              >
                <MenuItem value="single_choice">Single Choice</MenuItem>
                <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                <MenuItem value="true_false">True/False</MenuItem>
              </Select>
              {errors.answerType && <FormHelperText>{errors.answerType}</FormHelperText>}
            </StyledFormControl>

            <StyledTextField
              label="Difficulty"
              value={formData.difficulty}
              onChange={(e) => handleFieldChange('difficulty', e.target.value)}
              error={!!errors.difficulty}
              helperText={errors.difficulty}
            />

            <StyledTextField
              label="Question Mood"
              value={formData.questionMood}
              onChange={(e) => handleFieldChange('questionMood', e.target.value)}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <StyledTextField
              label="Question Topic"
              value={formData.questionTopic}
              onChange={(e) => handleFieldChange('questionTopic', e.target.value)}
            />

            <StyledTextField
              label="Question Bank"
              value={formData.questionBank}
              onChange={(e) => handleFieldChange('questionBank', e.target.value)}
            />

            <StyledTextField
              label="Time Limit (seconds)"
              type="number"
              value={formData.timeLimit}
              onChange={(e) => handleFieldChange('timeLimit', parseInt(e.target.value) || 30)}
            />
          </Box>

          {/* Question Clue */}
          <StyledTextField
            fullWidth
            label="Question Clue (Optional)"
            multiline
            rows={2}
            value={formData.questionClue}
            onChange={(e) => handleFieldChange('questionClue', e.target.value)}
          />

          {/* Answers Section */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#fff' }}>
                Answers ({formData.answers.length})
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addAnswer}
                disabled={formData.answers.length >= 6}
                sx={{ color: '#4CAF50' }}
              >
                Add Answer
              </Button>
            </Box>

            {errors.answers && (
              <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
                <Typography sx={{ color: '#ffcdd2' }}>{errors.answers}</Typography>
              </Alert>
            )}

            {formData.answers.map((answer, index) => (
              <AnswerBox key={index}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Chip
                    label={`${index + 1}`}
                    size="small"
                    sx={{
                      backgroundColor: answer.correct ? '#4CAF50' : '#666',
                      color: '#fff',
                      minWidth: '32px'
                    }}
                  />
                  <StyledTextField
                    fullWidth
                    label="Answer Text"
                    value={answer.text || ''}
                    onChange={(e) => handleAnswerChange(index, 'text', e.target.value)}
                  />
                  <Button
                    variant={answer.correct ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleAnswerChange(index, 'correct', !answer.correct)}
                    sx={{
                      backgroundColor: answer.correct ? '#4CAF50' : 'transparent',
                      borderColor: '#4CAF50',
                      color: answer.correct ? '#fff' : '#4CAF50',
                      minWidth: '80px'
                    }}
                  >
                    {answer.correct ? 'Correct' : 'Wrong'}
                  </Button>
                  <IconButton
                    onClick={() => removeAnswer(index)}
                    disabled={formData.answers.length <= 2}
                    sx={{ color: '#f44336' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <StyledTextField
                  fullWidth
                  label="Answer Reason (Optional)"
                  size="small"
                  value={answer.reason || ''}
                  onChange={(e) => handleAnswerChange(index, 'reason', e.target.value)}
                />
              </AnswerBox>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #333' }}>
        <Button onClick={handleClose} sx={{ color: '#ccc' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          sx={{
            backgroundColor: '#4CAF50',
            '&:hover': { backgroundColor: '#45a049' }
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default QuestionEditModal;
